import { DateInput, DateInputProps } from "@mantine/dates";
import { useTheme } from "@contexts/ThemeContext";

/**
 * JarvisDateInput - A themed DateInput component that automatically applies
 * the current theme colors to match the application's design system.
 *
 * Usage: Use this instead of Mantine's DateInput to get automatic theming.
 */
export const JarvisDateInput = (props: DateInputProps) => {
  const { colors: themeColors } = useTheme();

  const customDateParser: DateInputProps["dateParser"] = (input) => {
    // Parse DD-MM-YYYY format without clamping years
    const match = input.trim().match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (match) {
      const [, day, month, year] = match;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
    // Fallback to native parsing
    return new Date(input);
  };

  return (
    <DateInput
      {...props}
      dateParser={props.dateParser ?? customDateParser}
      styles={{
        ...props.styles,
        input: {
          backgroundColor: themeColors.inputBackground,
          color: themeColors.inputText,
          borderColor: themeColors.inputBorder,
          '&:focus': {
            borderColor: themeColors.primary,
          },
          '&::placeholder': {
            color: themeColors.inputPlaceholder,
          },
          '&:disabled': {
            backgroundColor: themeColors.inputBackground,
            color: themeColors.inputText,
            opacity: 0.6,
          },
        },
        calendarHeaderControl: {
          color: themeColors.text,
        },
        calendarHeaderLevel: {
          color: themeColors.text,
        },
        weekday: {
          color: themeColors.textSecondary ?? themeColors.text,
        },
        day: {
          color: themeColors.text,
          '&[data-selected]': {
            backgroundColor: themeColors.primary,
            color: themeColors.buttonText ?? '#FFFFFF',
          },
          '&[data-today]': {
            borderColor: themeColors.primary,
          },
        },
      }}
      popoverProps={{
        ...props.popoverProps,
        styles: {
          dropdown: {
            backgroundColor: themeColors.card,
            borderColor: themeColors.border,
          },
        },
      }}
    />
  );
};

export default JarvisDateInput;
